/**
 * SkillsPage — Manage Claude Code skills stored in Martha.
 * Split-pane: skill list (left) + markdown editor (right).
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Plus, Trash2, Save, Upload, Archive, Search, Terminal,
  Eye, EyeOff, Check, Copy, X, AlertTriangle, Sparkles,
} from 'lucide-react';
import {
  useSkills,
  useSkill,
  useCreateSkill,
  useUpdateSkill,
  useDeleteSkill,
  usePublishSkill,
  useEnvSkills,
  type Skill,
  type SkillStatus,
  type CreateSkillPayload,
  type UpdateSkillPayload,
} from '../../../api/skills';
import { useArchieEnvironments, type ArchieEnvironmentStatus } from '../../../api/orchestration';
import type { ArchieEnvironment } from '../../../api/environments';
import { ENV_COLORS } from '../../../api/environments';
import { StatusBadge, MetricCard, FieldLabel } from '../../../components/shared/Panel';
import { EmptyState } from '../../../components/shared/EmptyState';

// ─── Status → badge variant ──────────────────────────────────────────────────
const STATUS_VARIANT: Record<SkillStatus, 'success' | 'warning' | 'neutral'> = {
  published: 'success',
  draft: 'warning',
  archived: 'neutral',
};

// ─── Category badge colors ────────────────────────────────────────────────────
const CATEGORY_COLORS: Record<string, string> = {
  martha: 'bg-coral-100 text-coral-700',
  archie: 'bg-violet-100 text-violet-700',
  platform: 'bg-coral-100 text-coral-700',
  workflow: 'bg-violet-100 text-violet-700',
  general: 'bg-neutral-100 text-neutral-600',
  integration: 'bg-accent-100 text-accent-700',
  analysis: 'bg-amber-100 text-amber-700',
};

// ─── Copy Button ──────────────────────────────────────────────────────────────
function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [text]);
  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50 transition-all"
    >
      {copied ? <Check size={12} className="text-accent-600" /> : <Copy size={12} />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

// ─── Skill List Item ──────────────────────────────────────────────────────────
function SkillListItem({ skill, selected, onClick }: { skill: Skill; selected: boolean; onClick: () => void }) {
  const catCls = CATEGORY_COLORS[skill.category] || CATEGORY_COLORS.general;
  return (
    <div
      onClick={onClick}
      className={`px-3 py-2.5 rounded-lg cursor-pointer transition-all ${
        selected
          ? 'bg-coral-50/60 ring-1 ring-coral-500 shadow-warm-sm'
          : 'hover:bg-neutral-50'
      }`}
    >
      <div className="flex items-start justify-between gap-2 mb-0.5">
        <div className="flex items-center gap-2 min-w-0">
          <Terminal size={13} className="text-neutral-400 shrink-0" />
          <span className="text-[13px] font-semibold text-neutral-800 truncate">{skill.name}</span>
        </div>
        <StatusBadge variant={STATUS_VARIANT[skill.status]}>{skill.status}</StatusBadge>
      </div>
      <p className="text-xs text-neutral-400 line-clamp-1 pl-5 mb-1.5">
        {skill.description || 'No description'}
      </p>
      <div className="flex items-center gap-2 pl-5">
        <span className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${catCls}`}>{skill.category}</span>
        <span className="text-[11px] font-mono text-neutral-400">/{skill.slug}</span>
        <span className="text-[11px] text-neutral-300 ml-auto">v{skill.version}</span>
      </div>
    </div>
  );
}

// ─── Create Modal ─────────────────────────────────────────────────────────────
function CreateSkillModal({ onClose, onCreated, defaultCategory }: { onClose: () => void; onCreated: (id: string) => void; defaultCategory: string }) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const createMutation = useCreateSkill();

  useEffect(() => {
    setSlug(name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''));
  }, [name]);

  const handleSubmit = async () => {
    if (!name.trim() || !slug.trim()) return;
    try {
      const result = await createMutation.mutateAsync({ name, slug, description, category: defaultCategory });
      onCreated(result.data.id);
    } catch {
      // handled by mutation state
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg mx-4 rounded-xl border border-neutral-200 bg-white shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-100">
          <h3 className="text-sm font-semibold text-neutral-900">New Skill</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-neutral-100 text-neutral-400 transition-colors">
            <X size={16} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-neutral-600 mb-1.5">Name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Sprint Planning"
              autoFocus
              className="w-full px-3 py-2 rounded-lg border border-neutral-200 bg-white text-sm text-neutral-900 placeholder:text-neutral-300 focus:outline-none focus:ring-1 focus:ring-coral-500/20 focus:border-coral-300 transition-all"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-neutral-600 mb-1.5">
              Slug <span className="text-neutral-400">(used as /command)</span>
            </label>
            <div className="flex items-center">
              <span className="px-3 py-2 rounded-l-lg border border-r-0 border-neutral-200 bg-neutral-50 text-sm text-neutral-400 font-mono">/</span>
              <input
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                className="flex-1 px-3 py-2 rounded-r-lg border border-neutral-200 bg-white text-sm text-neutral-900 font-mono focus:outline-none focus:ring-1 focus:ring-coral-500/20 focus:border-coral-300 transition-all"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-neutral-600 mb-1.5">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What does this skill do?"
              rows={2}
              className="w-full px-3 py-2 rounded-lg border border-neutral-200 bg-white text-sm text-neutral-900 placeholder:text-neutral-300 focus:outline-none focus:ring-1 focus:ring-coral-500/20 focus:border-coral-300 transition-all resize-none"
            />
          </div>
          {createMutation.error && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200">
              <AlertTriangle size={14} className="text-red-500 shrink-0" />
              <p className="text-xs text-red-600">{(createMutation.error as Error).message}</p>
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-neutral-100">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-medium text-neutral-600 hover:bg-neutral-100 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!name.trim() || !slug.trim() || createMutation.isPending}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-coral-600 text-white hover:bg-coral-700 transition-all shadow-warm-sm disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {createMutation.isPending ? 'Creating…' : 'Create Skill'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Skill Editor (right panel) ───────────────────────────────────────────────
function SkillEditor({ skill, onClose }: { skill: Skill; onClose: () => void }) {
  const [content, setContent] = useState(skill.content);
  const [name, setName] = useState(skill.name);
  const [description, setDescription] = useState(skill.description);
  const [isUserInvocable, setIsUserInvocable] = useState(skill.isUserInvocable);
  const [showPreview, setShowPreview] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const updateMutation = useUpdateSkill();
  const deleteMutation = useDeleteSkill();
  const publishMutation = usePublishSkill();

  useEffect(() => {
    setContent(skill.content);
    setName(skill.name);
    setDescription(skill.description);
    setIsUserInvocable(skill.isUserInvocable);
    setShowPreview(false);
    setConfirmDelete(false);
  }, [skill.id, skill.content, skill.name, skill.description, skill.isUserInvocable]);

  const isDirty = content !== skill.content || name !== skill.name || description !== skill.description || isUserInvocable !== skill.isUserInvocable;

  const handleSave = async () => {
    await updateMutation.mutateAsync({ id: skill.id, payload: { content, name, description, isUserInvocable } });
  };

  const handlePublish = async () => {
    if (isDirty) await handleSave();
    await publishMutation.mutateAsync(skill.id);
  };

  const handleDelete = async () => {
    await deleteMutation.mutateAsync(skill.id);
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 's') {
      e.preventDefault();
      if (isDirty) handleSave();
    }
    if (e.key === 'Tab') {
      e.preventDefault();
      const ta = textareaRef.current;
      if (ta) {
        const start = ta.selectionStart;
        const end = ta.selectionEnd;
        setContent(content.slice(0, start) + '  ' + content.slice(end));
        requestAnimationFrame(() => { ta.selectionStart = ta.selectionEnd = start + 2; });
      }
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200 bg-white shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-mono text-neutral-500">/{skill.slug}</span>
            <StatusBadge variant={STATUS_VARIANT[skill.status]}>{skill.status}</StatusBadge>
            {isDirty && (
              <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700">unsaved</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setShowPreview(!showPreview)}
            className={`p-1.5 rounded transition-colors ${showPreview ? 'bg-neutral-100 text-neutral-700' : 'text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100'}`}
            title={showPreview ? 'Edit' : 'Preview'}
          >
            {showPreview ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
          {skill.status === 'draft' && (
            <button
              onClick={handlePublish}
              disabled={publishMutation.isPending}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-accent-600 text-white hover:bg-accent-700 transition-all disabled:opacity-40"
            >
              <Upload size={12} />
              Publish
            </button>
          )}
          {skill.status === 'published' && (
            <button
              onClick={() => updateMutation.mutate({ id: skill.id, payload: { status: 'archived' } })}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-neutral-200 text-neutral-600 hover:bg-neutral-50 transition-all"
            >
              <Archive size={12} />
              Archive
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={!isDirty || updateMutation.isPending}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-coral-600 text-white hover:bg-coral-700 transition-all shadow-warm-sm disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <Save size={12} />
            {updateMutation.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      {/* Metadata */}
      <div className="px-4 py-3 border-b border-neutral-100 bg-neutral-50/60 shrink-0">
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <FieldLabel className="block mb-1">Name</FieldLabel>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-2.5 py-1.5 rounded-lg border border-neutral-200 bg-white text-sm text-neutral-900 focus:outline-none focus:ring-1 focus:ring-coral-500/20 focus:border-coral-300 transition-all"
            />
          </div>
          <div>
            <FieldLabel className="block mb-1">Description</FieldLabel>
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-2.5 py-1.5 rounded-lg border border-neutral-200 bg-white text-sm text-neutral-900 focus:outline-none focus:ring-1 focus:ring-coral-500/20 focus:border-coral-300 transition-all"
            />
          </div>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-xs text-neutral-600 cursor-pointer">
            <input
              type="checkbox"
              checked={isUserInvocable}
              onChange={(e) => setIsUserInvocable(e.target.checked)}
              className="rounded border-neutral-300 text-coral-600 focus:ring-coral-500/20"
            />
            User invocable (/<span className="font-mono">{skill.slug}</span>)
          </label>
          <span className="text-xs text-neutral-400 font-mono ml-auto">
            v{skill.version} · {skill.installCount} installs · {skill.category}
          </span>
        </div>
      </div>

      {/* Editor / Preview */}
      <div className="flex-1 overflow-hidden relative">
        {showPreview ? (
          <div className="absolute inset-0 overflow-auto p-6 bg-white">
            <pre className="max-w-3xl mx-auto whitespace-pre-wrap font-mono text-[13px] text-neutral-700 leading-relaxed">
              {content || 'Empty skill — start writing in the editor.'}
            </pre>
          </div>
        ) : (
          <textarea
            ref={textareaRef}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onKeyDown={handleKeyDown}
            spellCheck={false}
            className="absolute inset-0 w-full h-full p-6 resize-none bg-white text-neutral-800 font-mono text-[13px] leading-relaxed border-none outline-none placeholder:text-neutral-300"
            placeholder={`---\nname: "${skill.name}"\ndescription: "${skill.description}"\nuser_invocable: true\n---\n\n# ${skill.name}\n\nWrite your skill content here…`}
          />
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-4 py-2.5 border-t border-neutral-100 bg-neutral-50/60 shrink-0">
        <div className="flex items-center gap-3">
          <span className="text-xs text-neutral-400">
            {content.length.toLocaleString()} chars · {content.split('\n').length} lines
          </span>
          <CopyButton text={content} />
        </div>
        <div className="flex items-center gap-2">
          {confirmDelete ? (
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-red-600">Delete this skill?</span>
              <button
                onClick={handleDelete}
                className="px-2.5 py-1 rounded-lg text-xs font-medium bg-red-600 text-white hover:bg-red-700 transition-colors"
              >
                Confirm
              </button>
              <button
                onClick={() => setConfirmDelete(false)}
                className="px-2.5 py-1 rounded-lg text-xs text-neutral-600 hover:bg-neutral-100 transition-colors"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setConfirmDelete(true)}
              className="p-1.5 rounded text-neutral-300 hover:text-red-500 hover:bg-red-50 transition-colors"
              title="Delete skill"
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Env Pill ─────────────────────────────────────────────────────────────────
function EnvPill({ env, selected, onClick }: {
  env: ArchieEnvironmentStatus;
  selected: boolean;
  onClick: () => void;
}) {
  const colors = ENV_COLORS[env.id] ?? ENV_COLORS['preview'];
  const isOffline = env.status === 'offline';
  return (
    <button
      onClick={onClick}
      disabled={isOffline}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-[11px] font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
        selected
          ? `ring-1 ring-coral-500 ${colors.bg} ${colors.border}`
          : `${colors.bg} ${colors.border} hover:opacity-80`
      }`}
    >
      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${
        env.status === 'online' ? 'bg-accent-500' :
        env.status === 'offline' ? 'bg-red-400' : 'bg-neutral-300'
      }`} />
      <span className={`font-mono font-bold px-1 py-0.5 rounded text-[10px] ${colors.badge}`}>
        {env.shortLabel}
      </span>
      {env.skillCount > 0 && (
        <span className="text-neutral-500">{env.skillCount}</span>
      )}
    </button>
  );
}

// ─── Category tabs ────────────────────────────────────────────────────────────
const CATEGORY_TABS = [
  { key: '', label: 'All Skills' },
  { key: 'archie', label: 'Archie' },
  { key: 'martha', label: 'Martha' },
] as const;

// ─── Main Page ────────────────────────────────────────────────────────────────

const SkillsPage: React.FC = () => {
  const [categoryFilter, setCategoryFilter] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedEnvId, setSelectedEnvId] = useState<string | null>(null);

  const { data: envsData } = useArchieEnvironments();
  const envs = envsData?.data ?? [];

  const isEnvFiltered = selectedEnvId !== null && selectedEnvId !== 'helsinki';

  const { data: localData, isLoading: localLoading, error: localError } = useSkills(
    isEnvFiltered ? undefined : (categoryFilter ? { category: categoryFilter } : undefined),
  );

  const { data: envSkillData, isLoading: envLoading, error: envError } = useEnvSkills(
    (selectedEnvId ?? 'helsinki') as ArchieEnvironment,
    categoryFilter ? { category: categoryFilter } : undefined,
  );

  const data = isEnvFiltered ? envSkillData : localData;
  const isLoading = isEnvFiltered ? envLoading : localLoading;
  const error = isEnvFiltered ? envError : localError;
  const { data: selectedSkillData } = useSkill(selectedId);

  const skills = data?.data ?? [];
  const selectedSkill = selectedSkillData?.data ?? null;

  const filteredSkills = searchQuery
    ? skills.filter((s) => {
        const q = searchQuery.toLowerCase();
        return s.name.toLowerCase().includes(q) || s.slug.toLowerCase().includes(q) || s.description.toLowerCase().includes(q);
      })
    : skills;

  const handleCreated = (id: string) => {
    setShowCreateModal(false);
    setSelectedId(id);
  };

  const published = skills.filter((s) => s.status === 'published').length;
  const drafts = skills.filter((s) => s.status === 'draft').length;

  return (
    <div className="flex flex-col h-full">
      {/* Page header */}
      <div className="px-6 pt-5 pb-4 border-b border-neutral-200 bg-white shrink-0">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h1 className="text-2xl font-semibold text-neutral-900 tracking-tight flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-coral-50 flex items-center justify-center">
                <Terminal size={18} className="text-coral-600" />
              </div>
              Skills
            </h1>
            <p className="text-sm text-neutral-500 mt-1 ml-12">
              Claude Code /commands served dynamically from Martha
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-coral-600 text-white hover:bg-coral-700 transition-all shadow-warm-sm"
          >
            <Plus size={14} />
            New Skill
          </button>
        </div>
        <div className="flex items-center justify-between ml-12">
          {/* Category tabs */}
          <div className="flex items-center gap-0.5 bg-neutral-100 rounded-lg p-0.5">
            {CATEGORY_TABS.map(tab => (
              <button
                key={tab.key}
                onClick={() => { setCategoryFilter(tab.key); setSelectedId(null); }}
                className={`px-3 py-1.5 rounded-md text-[11px] font-medium transition-colors ${
                  categoryFilter === tab.key
                    ? 'bg-white text-coral-700 shadow-sm'
                    : 'text-neutral-500 hover:text-neutral-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          {skills.length > 0 && (
            <div className="flex items-center gap-3">
              <MetricCard label="Total" value={skills.length} accent="neutral" />
              <MetricCard label="Published" value={published} accent="coral" />
              <MetricCard label="Drafts" value={drafts} accent="amber" />
            </div>
          )}
        </div>
        {/* Env pills row */}
        {envs.length > 0 && (
          <div className="flex items-center gap-1.5 mt-3 ml-12 flex-wrap">
            <button
              onClick={() => setSelectedEnvId(null)}
              className={`inline-flex items-center px-2.5 py-1 rounded-lg border text-[11px] font-medium transition-all ${
                !selectedEnvId
                  ? 'ring-1 ring-coral-500 bg-coral-50 border-coral-200 text-coral-700'
                  : 'bg-neutral-50 border-neutral-200 text-neutral-500 hover:bg-neutral-100'
              }`}
            >
              All
            </button>
            {envs.map(env => (
              <EnvPill
                key={env.id}
                env={env}
                selected={selectedEnvId === env.id}
                onClick={() => setSelectedEnvId(selectedEnvId === env.id ? null : env.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Split pane */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left: skill list */}
        <div className="w-[340px] shrink-0 flex flex-col border-r border-neutral-200 bg-white">
          {/* List toolbar */}
          <div className="px-4 pt-4 pb-3 border-b border-neutral-100">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400" />
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search skills…"
                className="w-full pl-8 pr-3 py-1.5 border border-neutral-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-coral-500/20 focus:border-coral-300 transition-all placeholder-neutral-300"
              />
            </div>
          </div>

          {/* Skill list */}
          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-12 text-sm text-neutral-400">
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                Loading skills…
              </div>
            ) : error ? (
              <div className="p-4 text-xs text-red-500">Failed to load skills</div>
            ) : filteredSkills.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-neutral-400">
                <Sparkles size={24} className="mb-2 text-neutral-300" />
                <p className="text-xs">{skills.length === 0 ? 'No skills yet' : 'No skills match filters'}</p>
              </div>
            ) : (
              <div className="p-2 space-y-0.5">
                {filteredSkills.map((skill) => (
                  <SkillListItem
                    key={skill.id}
                    skill={skill}
                    selected={selectedId === skill.id}
                    onClick={() => setSelectedId(skill.id)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right: editor */}
        <div className="flex-1 bg-white overflow-hidden">
          {selectedSkill ? (
            <SkillEditor key={selectedSkill.id} skill={selectedSkill} onClose={() => setSelectedId(null)} />
          ) : skills.length === 0 && !isLoading ? (
            <div className="h-full flex items-center justify-center">
              <EmptyState
                icon={<Sparkles size={24} className="text-coral-400" />}
                title="No skills yet"
                description="Skills are Claude Code /commands served dynamically from Martha. Create one to make it available on any machine with Martha connected."
                actions={
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-coral-600 text-white hover:bg-coral-700 transition-all shadow-warm-sm"
                  >
                    <Plus size={14} />
                    Create your first skill
                  </button>
                }
              />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-neutral-400">
              <Terminal size={32} className="mb-3 text-neutral-300" />
              <p className="text-sm font-medium text-neutral-500">Select a skill to edit</p>
              <p className="text-xs text-neutral-300 mt-1">Cmd+S to save · Publish to make it installable</p>
            </div>
          )}
        </div>
      </div>

      {showCreateModal && (
        <CreateSkillModal onClose={() => setShowCreateModal(false)} onCreated={handleCreated} defaultCategory={categoryFilter || 'archie'} />
      )}
    </div>
  );
};

export default SkillsPage;
